import { FC, forwardRef, ReactNode, Ref, useEffect, useState } from 'react';
import * as RadixAccordion from '@radix-ui/react-accordion';
import cn from 'classnames';

import { Badge, BadgeVariant } from '../../molecules/badge/Badge';
import { Container } from '../../atoms/container/Container';
import { Heading } from '../../../components/atoms/heading/Heading';
import { Icon, IconNames } from '../../atoms/icon/Icon';
import { Title } from '../../atoms/title/Title';

import './Accordion.scss';

export type AccordionChildrenBackgroundColors = 'white' | 'gray';
export type AccordionIcon = 'chevron' | 'plus-sign';
export type AccordionSize = 'tiny' | 'small' | 'medium' | 'large';

type AccordionRootProps<T> = Omit<T, 'ref'> & { ref?: Ref<HTMLDivElement> };

export type AccordionRootSingleProps = AccordionRootProps<RadixAccordion.AccordionSingleProps>;
export type AccordionRootMultipleProps = AccordionRootProps<RadixAccordion.AccordionMultipleProps>;

export const AccordionRoot = forwardRef<HTMLDivElement, AccordionRootSingleProps | AccordionRootMultipleProps>(
  ({ children, ...props }, ref: AccordionRootSingleProps['ref']) => (
    <RadixAccordion.Root {...props} ref={ref}>
      {children}
    </RadixAccordion.Root>
  ),
);
AccordionRoot.displayName = 'AccordionRoot';

export interface AccordionItemProps extends Omit<RadixAccordion.AccordionItemProps, 'ref'> {
  /**
   * Defines if accordion should have a border or not
   * @default true
   */
  border?: boolean;
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLDivElement>;
}
export const AccordionItem: FC<AccordionItemProps> = ({ children, border = true, classNames = [], ref, ...props }) => {
  const borderClass = border ? 'border' : 'border-none';

  const accItemClasses = cn(
    {
      'lsux-accordion__item': true,
      [`lsux-accordion__item--${borderClass}`]: true,
    },
    classNames,
  );

  return (
    <RadixAccordion.Item ref={ref} className={cn(accItemClasses)} {...props}>
      {children}
    </RadixAccordion.Item>
  );
};

/* AccordionHeader */
export const AccordionHeader: FC<RadixAccordion.AccordionHeaderProps> = (props) => <RadixAccordion.Header {...props} />;

interface AccordionTriggerCustomProps {
  /**
   * Defines the accordion icon
   * @default 'chevron'
   */
  accordionIcon?: AccordionIcon;
  /**
   * Badge next to Accordion title
   */
  badgeText?: string;
  /**
   * Badge variant
   */
  badgeVariant?: BadgeVariant;
  /**
   * Accordion title shown as heading
   * If true, the title is shown using a Heading element instead of a Title component
   * @default false
   */
  headingTitle?: boolean;
  /**
   * Accordion element for the heading instead of text
   * Overrides headingTitle props.
   * Using tappable elements in headingChildren could conflict with the accordion functionality.
   * @default undefined
   */
  headingChildren?: ReactNode | ReactNode[];
  /**
   * Hides the accordion toggle icon
   */
  hideToggle?: boolean;
  /**
   * Determines if the accordion is expanded or collapsed
   */
  isExpanded?: boolean;
  /**
   * Size of the accordion
   * @type 'small' | 'medium' | 'large'
   * @default 'large'
   */
  size?: AccordionSize;
  /**
   * Title of the accordion
   */
  title?: string;
  /**
   * Icon before the title
   */
  titleIcon?: IconNames;
  /**
   * Custom classes for the component.
   */
  classNames?: cn.Argument;
}

export interface AccordionTriggerProps
  extends AccordionTriggerCustomProps,
    Omit<RadixAccordion.AccordionTriggerProps, 'ref'> {
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLButtonElement>;
}
export const AccordionTrigger: FC<AccordionTriggerProps> = ({
  accordionIcon = 'chevron',
  badgeText,
  badgeVariant,
  children,
  headingChildren,
  headingTitle,
  isExpanded,
  size = 'large',
  title,
  titleIcon,
  hideToggle = false,
  classNames,
  ref,
  ...props
}) => {
  const classTrigger = 'lsux-accordion__trigger';
  const cnTrigger = cn(
    {
      [classTrigger]: true,
      [`${classTrigger}--size-${size}`]: size,
    },
    classNames,
  );

  return (
    <RadixAccordion.Trigger ref={ref} className={cnTrigger} {...props}>
      {children ? (
        children
      ) : (
        <Container background="none">
          <Container background="none" flexbox flexDirection="row" alignItems="center">
            {titleIcon && <Icon name={titleIcon} classNames={['mr-4']} size="large" />}
            {headingTitle ? (
              <Heading as="T16" text={title} classNames={['mr-4', 'm-0']} />
            ) : headingChildren ? (
              <>{headingChildren}</>
            ) : (
              <Title text={title} textSize="large" textWeight="semibold" classNames={['mr-4']} />
            )}
            {badgeText && <Badge bold text={badgeText} variant={badgeVariant} classNames={['ml-4']} />}
          </Container>
          {!hideToggle && (
            <Container
              background="none"
              classNames={cn({
                [`lsux-icon-container__toggle`]: true,
                [`lsux-icon-container__toggle-animated`]: accordionIcon === 'chevron',
              })}
            >
              {accordionIcon === 'chevron' ? (
                <Icon classNames={['lsux-icon--chevron']} color="N800" size="medium" name="arrow_chevron_down" />
              ) : (
                <span>
                  {isExpanded ? (
                    <Icon
                      name="edit_remove_minus"
                      size="medium"
                      classNames={[`lsux-icon--${accordionIcon}-plus-minus`]}
                    />
                  ) : (
                    <Icon
                      name="edit_add_plus"
                      size="medium"
                      classNames={[`lsux-icon--${accordionIcon}-plus--${isExpanded ? 'opened' : 'closed'}`]}
                    />
                  )}
                </span>
              )}
            </Container>
          )}
        </Container>
      )}
    </RadixAccordion.Trigger>
  );
};

export interface AccordionContentProps extends Omit<RadixAccordion.AccordionContentProps, 'ref'> {
  ref?: Ref<HTMLDivElement>;
}
export const AccordionContent: FC<RadixAccordion.AccordionContentProps> = forwardRef<
  HTMLDivElement,
  AccordionContentProps
>(({ children, className, ...props }, ref: AccordionContentProps['ref']) => (
  <RadixAccordion.Content className={cn('lsux-accordion__content', className)} {...props} ref={ref}>
    {children}
  </RadixAccordion.Content>
));
AccordionContent.displayName = 'AccordionContent';

export interface AccordionProps
  extends AccordionTriggerCustomProps,
    Omit<RadixAccordion.AccordionImplMultipleProps, 'type'> {
  /**
   * Indicates the accordion is initially open. Note: This value is overwritten by `isExpanded`.
   * @default false
   */
  initialOpen?: boolean;
  /**
   * Additional content children
   */
  children?: ReactNode | ReactNode[];
  /**
   * Additional inner children
   */
  innerChildren?: ReactNode | ReactNode[];
  /**
   * Defines if accordion should have a border or not
   * @default true
   */
  border?: boolean;
  /**
   * Disables the accordion
   */
  disabled?: boolean;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLDivElement>;
}

const AccordionComponent = forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      accordionIcon = 'chevron',
      badgeText,
      badgeVariant,
      border = true,
      children,
      disabled = false,
      classNames = [],
      headingTitle = false,
      headingChildren,
      initialOpen = false,
      innerChildren,
      isExpanded,
      size = 'large',
      title,
      titleIcon,
      ...props
    },
    ref: AccordionProps['ref'],
  ) => {
    const itemId = 'default-item-id';
    const [accValue, setAccValue] = useState(initialOpen ? [itemId] : []);

    useEffect(() => {
      if (isExpanded !== undefined) {
        setAccValue(isExpanded ? [itemId] : []);
      }
    }, [isExpanded]);

    const isOpen = isExpanded !== undefined ? isExpanded : accValue.includes(itemId);

    const handleAccValueChange = (value: string[]): void => {
      setAccValue(value);
    };

    const classAccordion = 'lsux-accordion';
    const cnAccordion = cn(classAccordion, classNames);

    const classAccordionContent = `${classAccordion}__content`;
    const cnContent = cn({
      [`${classAccordionContent}`]: true,
      [`${classAccordionContent}-expanded`]: isOpen,
      [`${classAccordionContent}-collapsed`]: !isOpen,
    });

    return (
      <AccordionRoot
        {...props}
        className={cnAccordion}
        type="multiple"
        value={accValue}
        onValueChange={handleAccValueChange}
        ref={ref}
      >
        <AccordionItem value={itemId} border={border} disabled={disabled} aria-expanded={`${isOpen}`}>
          <AccordionHeader>
            <AccordionTrigger
              size={size}
              title={title}
              isExpanded={isOpen}
              accordionIcon={accordionIcon}
              badgeText={badgeText}
              badgeVariant={badgeVariant}
              headingTitle={headingTitle}
              headingChildren={headingChildren}
              titleIcon={titleIcon}
            />
          </AccordionHeader>
          {innerChildren && (
            <Container>
              <div className={`${classAccordion}__inner-children-wrapper`}>{innerChildren}</div>
            </Container>
          )}
          <AccordionContent className={cnContent}>{children}</AccordionContent>
        </AccordionItem>
      </AccordionRoot>
    );
  },
) as FC<AccordionProps>;

AccordionComponent.displayName = 'Accordion';

type AccordionContentComponent = FC<AccordionContentProps>;
type AccordionHeaderComponent = FC<RadixAccordion.AccordionHeaderProps>;
type AccordionItemComponent = FC<AccordionItemProps>;
type AccordionRootComponent = FC<RadixAccordion.AccordionMultipleProps | RadixAccordion.AccordionSingleProps>;
type AccordionTriggerComponent = FC<AccordionTriggerProps>;

type AccordionComp = FC<AccordionProps> & {
  Content: AccordionContentComponent;
  Header: AccordionHeaderComponent;
  Item: AccordionItemComponent;
  Root: AccordionRootComponent;
  Trigger: AccordionTriggerComponent;
};

/**
 * Accordions are layout components that can expand and collapse
 */
export const Accordion = AccordionComponent as AccordionComp;

Accordion.Content = AccordionContent;
Accordion.Header = AccordionHeader;
Accordion.Item = AccordionItem;
Accordion.Root = AccordionRoot;
Accordion.Trigger = AccordionTrigger;
